import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { MobileVerificationOtpResult } from 'types/data-processor.types';
import {
  FormHandlers,
  FormState,
} from '../types/onboarding-account-setup-types';
import { useFormState } from '../hooks/use-form-state.hook';

export interface OnboardingFormStateValue {
  formState: FormState;
  requestOtpStatus: { isPending: boolean };
  setUserProfileStatus: { isError: boolean; isPending: boolean };
  verifyOtpStatus: { isPending: boolean };
}

export interface OnboardingFormActionsValue extends FormHandlers {
  validateMobileNumber: () => boolean;
  validateVerificationCode: () => boolean;
  validatePersonalInformation: () => boolean;
  submitMobileNumber: () => Promise<MobileVerificationOtpResult | undefined>;
  submitVerificationCode: () => Promise<
    MobileVerificationOtpResult | undefined
  >;
  submitPersonalInformation: () => Promise<void>;
  resendOtp: () => Promise<MobileVerificationOtpResult | undefined>;
}

export const OnboardingFormStateContext =
  createContext<OnboardingFormStateValue | null>(null);

export const OnboardingFormActionsContext =
  createContext<OnboardingFormActionsValue | null>(null);

interface OnboardingFormProviderProps {
  children: ReactNode;
  primaryMobileNumber?: string;
}

export const OnboardingFormProvider = ({
  children,
  primaryMobileNumber,
}: OnboardingFormProviderProps) => {
  const {
    formState,
    formHandlers,
    requestOtpStatus,
    setUserProfileStatus,
    validateMobileNumber,
    validateVerificationCode,
    validatePersonalInformation,
    submitMobileNumber,
    submitVerificationCode,
    submitPersonalInformation,
    resendOtp,
    verifyOtpStatus,
  } = useFormState({ primaryMobileNumber });

  const stateValue = useMemo<OnboardingFormStateValue>(
    () => ({
      formState,
      requestOtpStatus,
      setUserProfileStatus,
      verifyOtpStatus,
    }),
    [formState, requestOtpStatus, setUserProfileStatus, verifyOtpStatus],
  );

  const actionsValue = useMemo<OnboardingFormActionsValue>(
    () => ({
      ...formHandlers,
      validateMobileNumber,
      validateVerificationCode,
      validatePersonalInformation,
      submitMobileNumber,
      submitVerificationCode,
      submitPersonalInformation,
      resendOtp,
    }),
    [
      formHandlers,
      validateMobileNumber,
      validateVerificationCode,
      validatePersonalInformation,
      submitMobileNumber,
      submitVerificationCode,
      submitPersonalInformation,
      resendOtp,
    ],
  );

  return (
    <OnboardingFormStateContext.Provider value={stateValue}>
      <OnboardingFormActionsContext.Provider value={actionsValue}>
        {children}
      </OnboardingFormActionsContext.Provider>
    </OnboardingFormStateContext.Provider>
  );
};

export const useOnboardingFormState = (): OnboardingFormStateValue => {
  const ctx = useContext(OnboardingFormStateContext);
  if (!ctx) {
    throw new Error(
      'useOnboardingFormState must be used within <OnboardingFormProvider>',
    );
  }
  return ctx;
};

export const useOnboardingFormActions = (): OnboardingFormActionsValue => {
  const ctx = useContext(OnboardingFormActionsContext);
  if (!ctx) {
    throw new Error(
      'useOnboardingFormActions must be used within <OnboardingFormProvider>',
    );
  }
  return ctx;
};
